import React from "react";
import PropTypes from "prop-types";
import { store } from "reducers";
import { setAgentSessionToken } from "actions/agentDeployActions";
import {
  AgentforceMFEModalScrollBody as ScrollBody,
  AgentforceMFEModalAgentTurn as AgentTurn,
  AgentforceMFEModalUserTurn as UserTurn
} from "@trailhead-emu/design-system";
import AgentChatInputBar from "./AgentChatInputBar";
import WelcomeState from "./WelcomeState";
import UnauthenticatedState from "./UnauthenticatedState";
import NoOrgState from "../tds2_shim/agentforce/NoOrgState";
import OverloadedState from "./OverloadedState";
import PlanningWorklist from "../tds2_shim/agentforce/PlanningWorklist";
import ThinkingIndicator from "../tds2_shim/agentforce/ThinkingIndicator";
import ThinkingSummaryDisclosure from "../tds2_shim/agentforce/ThinkingSummaryDisclosure";
import ConnectionErrorState from "../tds2_shim/agentforce/ConnectionErrorState";
import LlmGatewayErrorState from "../tds2_shim/agentforce/LlmGatewayErrorState";
import CompilationErrorState from "../tds2_shim/agentforce/CompilationErrorState";
import DeployErrorState from "../tds2_shim/agentforce/DeployErrorState";
import DeploySuccessState from "../tds2_shim/agentforce/DeploySuccessState";
import CodeArtifactCard from "../tds2_shim/agentforce/CodeArtifactCard";
import formatAgentforceChatTime from "../tds2_shim/agentforce/formatAgentforceChatTime";
import ChatMarkdown from "./ChatMarkdown";

const LLM_GATEWAY_ERROR_REGEX = /^LLM Gateway error\b/i;

// Default starter prompt the "Build a Help Agent" welcome pill pre-fills when a
// unit declares no welcome of its own. Mirrors PROMPTS.builderStarter in the
// headless-agent-creation prototype (the single-pill Help Agent experience).
const DEFAULT_HELP_AGENT_PROMPT =
  "Create a Reservation Agent, name it Reservation Management and Guest Complaint Agent, " +
  "name it Guest Complaints. As this service agent will be used on our help site, name it " +
  "'Bookings Help Agent', and set the agent API name to 'bookings_help_agent'.";

// Maps internal task status to PlanningWorklist step state.
const TASK_STATUS_TO_STEP_STATE = {
  complete: "completed",
  in_progress: "in_progress",
  error: "error",
  retrying: "retrying",
  pending: "disabled"
};

function tasksToWorklistSteps(tasks) {
  return tasks.map((t) => ({
    label: t.label || t.id,
    state: TASK_STATUS_TO_STEP_STATE[t.status] || "disabled",
    // Per-step sub-line (e.g. the message on the active step, or the
    // "fixing & retrying" summary with the compile error). Previously dropped.
    detail: t.detail || null
  }));
}

// Truncates long text for a single-line detail sub-row.
function truncateDetail(text, max) {
  const s = String(text || "");
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

// Builds the "fixing & retrying" sub-line from a step_retry event, folding in
// the first compile/deploy error so the learner SEES what went wrong instead of
// just watching a step turn red.
function retryDetail(json) {
  const base = json.message || "Fixing an issue and retrying…";
  const first = json.errors && json.errors[0];
  const errText = first ? first.message || first : null;
  return errText ? base + " (" + truncateDetail(errText, 140) + ")" : base;
}

// Maps backend stage events to human-readable labels for the thinking indicator.
const STAGE_LABELS = {
  awaiting_llm: "Understanding your request",
  generate_script: "Preparing to generate AgentScript",
  generating_yaml: "Generating AgentScript",
  compile_and_deploy: "Starting deployment"
};

// Summary line rendered above an assistant message once its thinking phase
// has finished — the chevron disclosure collapses the per-stage list under it.
const THINKING_DONE_SUMMARY = "Done! Review your agent's drafted steps.";

// Maps the raw stage names accumulated during a turn into the user-facing
// labels used in both the live thinking indicator and the post-turn
// disclosure list. Unknown stages fall through verbatim.
function stagesToTraceSteps(stages) {
  if (!stages || stages.length === 0) return [];
  return stages.map((s) => STAGE_LABELS[s] || s);
}

const STATUS_IDLE = "idle";
const STATUS_CONNECTING = "connecting";
const STATUS_STREAMING = "streaming";
const STATUS_ERROR = "error";

// Deploy step definitions for task progression
const DEPLOY_STEPS = [
  { id: "extracting", label: "Extracting script" },
  { id: "authenticating", label: "Authenticating" },
  { id: "compiling", label: "Compiling" },
  { id: "publishing", label: "Publishing" },
  { id: "deploying", label: "Deploying metadata" },
  { id: "activating", label: "Activating" }
];

// Maps deploy step names to error/done states
const ERROR_STEPS = ["compilation_error", "deploy_error", "auth_error"];

// Placeholder shown in the (disabled) chat input when the user is logged out,
// alongside the UnauthenticatedState login panel — invites the user to log in
// rather than repeating the generic authenticated placeholder.
const LOGGED_OUT_PLACEHOLDER = "Log in or sign up to start building your agent...";

// Placeholder shown in the (disabled) chat input when the user is logged in but
// has no connected playground org, alongside the NoOrgState panel — invites the
// user to create a playground rather than repeating the generic placeholder.
const NO_ORG_PLACEHOLDER = "Create a playground to start building your agent...";

/**
 * AgentChat — TDS-aligned streaming chat component for agent provisioning.
 * React 16 class component. Speaks OpenAI Chat Completions streaming format
 * and renders tool calls, skill banners, task progression inline.
 */
export class ChatContainer extends React.Component {
  static propTypes = {
    apiUrl: PropTypes.string,
    unitId: PropTypes.string,
    contentUid: PropTypes.string,
    unitTitle: PropTypes.string,
    orgId: PropTypes.string,
    agentName: PropTypes.string,
    placeholder: PropTypes.string,
    hideDisclaimer: PropTypes.bool,
    welcomeMascotUrl: PropTypes.string,
    errorMascotUrl: PropTypes.string,
    welcomeServiceIconUrl: PropTypes.string,
    welcomeSalesIconUrl: PropTypes.string,
    // Unit-configured welcome screen (from agent_chat.welcome). When present,
    // its pills replace the built-in Sales/Service defaults. Shape:
    // { title, subtitle, pills: [{ label, prompt, variant, icon, track }] }.
    welcomeConfig: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      pills: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          prompt: PropTypes.string,
          variant: PropTypes.string,
          icon: PropTypes.string,
          track: PropTypes.string
        })
      )
    }),
    isAuthenticated: PropTypes.bool,
    hasConnectedOrg: PropTypes.bool,
    // Server-computed non-consuming capacity peek (Agent::ConcurrencyLimiter
    // #capacity_available?). When false AND no org is connected, we show the calm
    // "at capacity" panel instead of the create-playground panel, so no playground
    // is provisioned for a learner the first-turn gate would reject. Defaults true
    // (fail open — the pool never blocks the create-playground entry by default).
    poolHasCapacity: PropTypes.bool,
    helpUrl: PropTypes.string,
    // Optional deep-link to the runtime/SSE logs, surfaced as "View runtime logs"
    // on the connection-error state. Defaults to null so no broken link ships
    // until a real logs URL is wired in (dark-launch, mirrors showSharing).
    runtimeLogsUrl: PropTypes.string,
    orgRequestState: PropTypes.string,
    orgRequestStatusUrl: PropTypes.string,
    orgRequestAutoLogin: PropTypes.bool,
    errorMessage: PropTypes.string,
    loginUrl: PropTypes.string,
    initialMessages: PropTypes.array,
    deployMetadata: PropTypes.object,
    sessionLocked: PropTypes.bool,
    allowSessionReset: PropTypes.bool,
    // Trophy mascot + social-share wiring for the completion success state, shown
    // once the learner passes the challenge (locked). `showSharing` gates the
    // share row and defaults to false; `sharing` is only consumed when it's true.
    successMascotUrl: PropTypes.string,
    showSharing: PropTypes.bool,
    sharing: PropTypes.object
  };

  static defaultProps = {
    apiUrl: "/api/v1/agent/chat",
    agentName: "Agent",
    placeholder: "Describe the help agent you want to build...",
    hideDisclaimer: false,
    isAuthenticated: true,
    hasConnectedOrg: true,
    poolHasCapacity: true,
    loginUrl: "/login",
    initialMessages: [],
    deployMetadata: null,
    sessionLocked: false,
    allowSessionReset: false,
    welcomeConfig: null,
    successMascotUrl: null,
    showSharing: false,
    sharing: null,
    runtimeLogsUrl: null
  };

  state = {
    messages: [],
    inputValue: "",
    status: STATUS_IDLE,
    locked: false,
    error: null,
    // Sitewide concurrency pool: latched true when a turn is refused with a
    // 503 `pool_full` (the enforce_concurrency gate rejected admission because
    // Agent Chat is at capacity). Renders the calm full-panel OverloadedState
    // instead of the red connection error — the learner never entered the loop,
    // so it's not a turn failure and there's nothing to "retry" in the error
    // sense; they just re-attempt admission. Cleared on a successful re-entry.
    poolFull: false,
    selectedTrack: null,
    // TDS model: tasks for progression, activeSkill, toolCalls on messages
    tasks: [],
    activeSkill: null,
    agentStages: [],
    // Per-tool activity feed shown under the worklist during a build (running/ok/
    // error rows), giving visibility into what happens inside each coarse step.
    deployActivity: []
  };

  abortController = null;
  scrollRef = null;
  // Sentinel rendered at the end of the thread; we scroll it into view to keep
  // the latest message visible. `scrollIntoView({ block: 'end' })` works whether
  // the actual scroll container is the canvas or the page window, and it
  // respects `scroll-margin-bottom` so the last bubble clears the input dock
  // instead of being hidden behind it.
  endRef = null;
  idCounter = 0;

  generateId() {
    this.idCounter += 1;
    return "msg-" + Date.now() + "-" + this.idCounter;
  }

  componentDidMount() {
    const { initialMessages, sessionLocked } = this.props;

    if (initialMessages && initialMessages.length > 0) {
      const messages = initialMessages.map((msg, idx) => ({
        id: "restored-" + idx,
        role: msg.role,
        content: msg.content,
        createdAt: new Date()
      }));
      this.setState({ messages, locked: sessionLocked });
    } else if (sessionLocked) {
      this.setState({ locked: true });
    }

    // When the learner passes the hands-on challenge, the sibling Challenge
    // component fires a document `trailhead` CustomEvent with
    // detail.type === 'challengeComplete'. Lock the chat so the learner can't
    // keep editing a deployed/graded agent, and surface the completed state
    // (T2-4). Persist the lock server-side so a page refresh stays locked.
    document.addEventListener("trailhead", this.handleTrailheadEvent);
  }

  componentWillUnmount() {
    if (this.abortController) {
      this.abortController.abort();
    }
    document.removeEventListener("trailhead", this.handleTrailheadEvent);
  }

  // Fires when the sibling Challenge component dispatches the document-level
  // `trailhead`/`challengeComplete` CustomEvent (Challenge.jsx). On the
  // agent-chat unit page there is exactly one Challenge, so an unqualified match
  // is correct today. The event carries no unit id, so if a future layout ever
  // hosts this chat alongside another challenge, that challenge's completion
  // would also lock the chat — revisit (filter by a unit id on the payload) if
  // that layout ever ships.
  handleTrailheadEvent = (event) => {
    const detail = event && event.detail;
    if (!detail) return;
    if (detail.type === "challengeComplete") {
      this.lockOnCompletion();
    } else if (detail.type === "challengeRetake") {
      // The learner clicked "Retake" in the sibling Challenge component. Unlock
      // so they can chat with / rebuild their agent again this session.
      this.unlockOnRetake();
    }
  };

  // Locks the chat locally and persists the lock to Redis so the completed
  // state survives a refresh. The server already refuses further turns on a
  // locked session (chat#create short-circuits), so this is defense-in-depth
  // plus the source of truth for the rehydrated `sessionLocked` prop.
  lockOnCompletion = () => {
    if (this.state.locked) return;
    this.setState({ locked: true, status: STATUS_IDLE });

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const headers = { "Content-Type": "application/json" };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken.content;

    // Fire-and-forget: a failed persist only means the next page load won't
    // start locked — the local UI is already correct for this session. We send
    // org_id (same as handleResetSession) so the server scopes the lock to the
    // exact user_orgs row the panel will read back via `sessionLocked` on the
    // next render; without it the write could land in a different session hash
    // than the read for a user with multiple orgs on the same org_id.
    fetch(this.props.apiUrl + "/lock", {
      method: "POST",
      headers,
      body: JSON.stringify({ unit_id: this.props.unitId, content_uid: this.props.contentUid, org_id: this.props.orgId }),
      credentials: "same-origin"
    }).catch(() => {});
  };

  // Unlocks the chat when the learner retakes the challenge. Mirrors
  // lockOnCompletion inverted: flips `locked` off (keeping the conversation
  // intact — this is NOT a reset) and clears the server-side Redis lock so
  // chat#create stops short-circuiting and accepts turns again. The unlock is
  // session-only: durable completion still drives `sessionLocked` on the next
  // page load, so a refresh re-locks (parity with the left column's retake form).
  unlockOnRetake = () => {
    if (!this.state.locked) return;
    this.setState({ locked: false });

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const headers = { "Content-Type": "application/json" };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken.content;

    // Fire-and-forget: a failed clear only means the server keeps refusing turns
    // until the next load — the local UI is already unlocked. org_id scopes the
    // write to the same session hash the lock used (see lockOnCompletion).
    fetch(this.props.apiUrl + "/unlock", {
      method: "POST",
      headers,
      body: JSON.stringify({ unit_id: this.props.unitId, content_uid: this.props.contentUid, org_id: this.props.orgId }),
      credentials: "same-origin"
    }).catch(() => {});
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.messages !== this.state.messages ||
      prevState.status !== this.state.status ||
      prevState.tasks !== this.state.tasks
    ) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      // Prefer the sentinel — it respects `scroll-margin-bottom` so the last
      // message clears the input dock. Fall back to the canvas's scrollTop
      // when the sentinel isn't mounted yet (e.g. welcome state).
      if (this.endRef && typeof this.endRef.scrollIntoView === "function") {
        this.endRef.scrollIntoView({ block: "end", behavior: "auto" });
        return;
      }
      const el = this.scrollRef;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  // ─── Input Handling ────────────────────────────────────────────────────

  handleSend = () => {
    const { inputValue, status, locked } = this.state;
    const trimmed = inputValue.trim();

    if (!trimmed || locked || status === STATUS_STREAMING || status === STATUS_CONNECTING) return;

    const userMessage = {
      id: this.generateId(),
      role: "user",
      content: trimmed,
      createdAt: new Date()
    };

    this.setState(
      (prevState) => ({
        messages: [...prevState.messages, userMessage],
        inputValue: "",
        status: STATUS_CONNECTING,
        error: null,
        tasks: [],
        activeSkill: null,
        agentStages: []
      }),
      () => {
        this.streamChat(this.state.messages);
      }
    );
  };

  handleRetry = () => {
    const { messages, status } = this.state;
    // A retry re-sends the last user message, so a second tap while one is in
    // flight starts a parallel turn — e.g. a second deploy racing the first.
    if (messages.length === 0 || status === STATUS_STREAMING || status === STATUS_CONNECTING) return;
    this.setState(
      {
        status: STATUS_CONNECTING,
        error: null,
        tasks: [],
        activeSkill: null,
        agentStages: []
      },
      () => {
        this.streamChat(messages);
      }
    );
  };

  // Sitewide concurrency pool: the learner tapped "Try again" on the
  // OverloadedState panel. Clear the latch and re-attempt the refused turn by
  // re-streaming the same messages — same real endpoint, same enforce_concurrency
  // gate. If a seat has since freed up the turn proceeds normally; if the pool is
  // still full the 503 path re-latches poolFull and the panel returns. We only
  // reach this after a turn was attempted (so messages are non-empty), but guard
  // defensively: with no messages, just clear the latch back to idle.
  handlePoolRetry = () => {
    const { status } = this.state;
    if (status === STATUS_STREAMING || status === STATUS_CONNECTING) return;
    const hasMessages = this.state.messages.length > 0;
    this.setState(
      {
        status: hasMessages ? STATUS_CONNECTING : STATUS_IDLE,
        poolFull: false,
        error: null,
        tasks: [],
        activeSkill: null,
        agentStages: []
      },
      () => {
        if (this.state.messages.length > 0) this.streamChat(this.state.messages);
      }
    );
  };

  // "Try again" on the PRE-admission OverloadedState (shown in place of the
  // create-playground panel when the pool was full at page load). There's no
  // refused turn to re-stream and no seat held here — the gate fired before any
  // provisioning — so re-taking the capacity peek means a full reload: the server
  // recomputes poolHasCapacity, and if a seat has since freed the page comes back
  // as the normal create-playground panel.
  handleCapacityRecheck = () => {
    window.location.reload();
  };

  handleAbort = () => {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.setState({ status: STATUS_IDLE, activeSkill: null });
  };

  // Test-mode affordance (gated by the allowSessionReset prop, which is driven
  // by the :th_headless_agent Split config). Clears the server-side message
  // history via DELETE /api/v1/agent/chat, then resets local state back to the
  // welcome screen. The DELETE endpoint is self-scoped to the current user and
  // only clears messages — it does not touch the deployed agent.
  //
  // We only clear the local UI AFTER the server confirms the delete. Clearing
  // optimistically would make the chat *look* reset while Redis still held the
  // messages, so a page refresh would re-hydrate the "deleted" conversation —
  // the original bug this flow shipped with. We also send org_id alongside
  // unit_id so the server scopes the session deterministically instead of
  // relying on a Vault-dependent org lookup that can transiently fail (422).
  handleResetSession = async () => {
    if (!this.props.allowSessionReset) return;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const headers = { "Content-Type": "application/json" };
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken.content;
    }

    let ok = false;
    try {
      const response = await fetch(this.props.apiUrl, {
        method: "DELETE",
        headers,
        body: JSON.stringify({ unit_id: this.props.unitId, content_uid: this.props.contentUid, org_id: this.props.orgId }),
        credentials: "same-origin"
      });
      ok = response.ok;
    } catch (e) {
      ok = false;
    }

    if (!ok) {
      // Leave the conversation intact so the UI matches what's actually in
      // Redis. Surfacing the error tells the tester the reset did not take.
      this.setState({ error: "Could not reset the session. Please try again." });
      return;
    }

    this.setState({
      messages: [],
      inputValue: "",
      status: STATUS_IDLE,
      locked: false,
      error: null,
      selectedTrack: null,
      tasks: [],
      activeSkill: null,
      agentStages: []
    });
  };

  handlePromptPill = (text, track = null) => {
    this.setState({ inputValue: text, selectedTrack: track }, () => {
      const el = document.getElementById("agent-chat-input");
      if (el) el.focus();
    });
  };

  // ─── SSE Streaming ─────────────────────────────────────────────────────

  async streamChat(messages) {
    this.abortController = new AbortController();

    const lastUserMsg = messages.filter((m) => m.role === "user").pop();

    // Appends a new stage to agentStages (deduplicates — same stage won't appear
    // twice). Stages are also tracked in a local (turnStages) because setState is
    // batched (React 19 createRoot never flushes synchronously), so the
    // end-of-turn thinkingTrace snapshot below can't read them back from state.
    const turnStages = [];
    const pushStage = (stage) => {
      if (turnStages.includes(stage)) return;
      turnStages.push(stage);
      this.setState((prevState) => {
        if (prevState.agentStages.includes(stage)) return null;
        return { agentStages: [...prevState.agentStages, stage] };
      });
    };

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const headers = { "Content-Type": "application/json" };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken.content;
    }

    try {
      const response = await fetch(this.props.apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: lastUserMsg ? lastUserMsg.content : "",
          stream: true,
          org_id: this.props.orgId,
          unit_id: this.props.unitId,
          content_uid: this.props.contentUid,
          agent_track: this.state.selectedTrack
        }),
        signal: this.abortController.signal,
        credentials: "same-origin"
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Sitewide concurrency pool at capacity: the enforce_concurrency gate
        // refused a seat and rendered a pre-stream 503 with code `pool_full`
        // (errors[0].code). The learner never entered the agentic loop, so this
        // is NOT a turn error — surface the calm full-panel OverloadedState and
        // let them re-attempt admission, rather than the red connection error.
        const code = errorData.errors?.[0]?.code || errorData.code;
        if (response.status === 503 && code === "pool_full") {
          this.setState({ status: STATUS_IDLE, poolFull: true, activeSkill: null });
          return;
        }
        // ContentApiSupport renders {errors: [{message, ...}]}; older paths
        // and SSE error frames render {error: '...'}. Read both.
        const message = errorData.errors?.[0]?.message || errorData.error || "Request failed: " + response.status;
        throw new Error(message);
      }

      this.setState({ status: STATUS_STREAMING, deployActivity: [] });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let assistantToolCalls = [];
      let buffer = "";
      let currentEventType = "";

      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;
        const value = result.value;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];

          // Track event type
          if (line.startsWith("event:")) {
            currentEventType = line.slice(6).trim();
            continue;
          }

          // Skip comments/keepalives
          if (line.startsWith(":") || line.trim() === "") {
            if (line.trim() === "") currentEventType = "";
            continue;
          }

          if (!line.startsWith("data:")) continue;

          const data = line.slice(5).trim();
          if (data === "[DONE]") break;

          // Handle metadata events (status, skill, task updates)
          if (currentEventType === "metadata" || currentEventType === "status") {
            try {
              const metaJson = JSON.parse(data);
              this.handleMetadataEvent(metaJson, currentEventType);
            } catch (_) {
              // Non-JSON metadata line — skip
            }
            currentEventType = "";
            continue;
          }

          // Backend stage events signal what the agent is actually doing.
          if (currentEventType === "stage") {
            try {
              const stageJson = JSON.parse(data);
              if (stageJson.stage) pushStage(stageJson.stage);
            } catch (e) {
              // ignore
            }
            currentEventType = "";
            continue;
          }

          // Backend `script_ready` carries the cached AgentScript body.
          if (currentEventType === "script_ready") {
            try {
              const scriptJson = JSON.parse(data);
              if (scriptJson && scriptJson.script) {
                this.attachScriptArtifact(scriptJson.script, scriptJson.developer_name);
              }
            } catch (e) {
              // ignore parse errors
            }
            currentEventType = "";
            continue;
          }

          if (currentEventType === "compile_error") {
            try {
              const errJson = JSON.parse(data);
              this.attachCompilationError(errJson.errors || []);
            } catch (e) {
              // ignore parse errors
            }
            currentEventType = "";
            continue;
          }

          if (currentEventType === "deploy_error") {
            try {
              const errJson = JSON.parse(data);
              this.attachDeployError(errJson.error || "", errJson.code);
            } catch (e) {
              // ignore parse errors
            }
            currentEventType = "";
            continue;
          }

          if (currentEventType === "deploy_result") {
            try {
              const deployData = JSON.parse(data);
              this.handleDeployResult(deployData);
            } catch (e) {
              console.error("[AgentChat] Failed to process deploy_result:", e);
            }
            currentEventType = "";
            continue;
          }

          if (currentEventType === "error") {
            try {
              const errorData = JSON.parse(data);
              throw new Error(errorData.error || "Stream error");
            } catch (e) {
              if (e.message !== "Stream error") throw e;
            }
            currentEventType = "";
            continue;
          }

          // Standard OpenAI chat completion chunk
          try {
            const parsed = JSON.parse(data);
            const choices = parsed.choices;
            if (!Array.isArray(choices) || choices.length === 0) {
              currentEventType = "";
              continue;
            }

            const choice = choices[0];
            const delta = choice.delta;

            if (choice.finish_reason) {
              currentEventType = "";
              continue;
            }

            if (!delta) {
              currentEventType = "";
              continue;
            }

            // Content delta
            if (delta.content) {
              assistantContent += delta.content;
              this.updateAssistantMessage(assistantContent, assistantToolCalls);
            }

            // Tool call deltas
            if (delta.tool_calls && delta.tool_calls.length > 0) {
              assistantToolCalls = this.mergeToolCallDeltas(assistantToolCalls, delta.tool_calls);
              this.updateAssistantMessage(assistantContent, assistantToolCalls);
            }
          } catch (e) {
            // ignore parse errors for non-JSON lines
          }

          currentEventType = "";
        }
      }

      // Final update
      if (assistantContent || assistantToolCalls.length > 0) {
        this.updateAssistantMessage(assistantContent, assistantToolCalls);
      }

      // Snapshot the per-turn thinking trace onto the just-finished assistant
      // message so the disclosure can re-expand it later. Only attach when
      // we actually have an assistant bubble to anchor to and at least one
      // stage event was observed during the turn.
      if (assistantContent && turnStages.length > 0) {
        this.mergeIntoAssistantMessage({
          thinkingTrace: {
            summary: THINKING_DONE_SUMMARY,
            steps: stagesToTraceSteps(turnStages)
          }
        });
      }

      // A step still "retrying" when the turn ends never got its retry (e.g. the
      // model saved a fix but didn't redeploy). Show it as failed rather than
      // leave a row that looks like it's still working.
      this.setState((prevState) => ({
        status: STATUS_IDLE,
        activeSkill: null,
        tasks: prevState.tasks.map((t) => (t.status === "retrying" ? { ...t, status: "error" } : t))
      }));
    } catch (error) {
      if (error.name === "AbortError") {
        this.setState({ status: STATUS_IDLE });
        return;
      }
      this.setState({
        status: STATUS_ERROR,
        error: error.message,
        activeSkill: null
      });
    }
  }

  handleMetadataEvent(json, eventType) {
    if (!json || typeof json !== "object") return;

    const type = json.type;

    // Per-tool activity feed — visibility into what the agent is doing inside a
    // coarse step (e.g. "Editing the agent script", "Deploying metadata").
    if (type === "activity") {
      this.handleActivityEvent(json);
      return;
    }

    // Non-terminal "hit a snag, auto-correcting" cue. Carries the errors so we
    // can show WHAT went wrong; checked before the bare-`step` routing because a
    // step_retry frame also carries a `step`.
    if (type === "step_retry") {
      this.handleStepRetry(json);
      return;
    }

    // Handle deploy status events (the six coarse stages).
    if (eventType === "status" && json.step) {
      this.handleDeployStep(json);
      return;
    }

    if (type === "skill_activated") {
      this.setState({
        activeSkill: { id: json.skillId || "deploy", name: json.skillName || "Publishing" }
      });
    } else if (type === "skill_deactivated") {
      this.setState({ activeSkill: null });
    } else if (type === "task_update") {
      this.setState((prevState) => {
        const tasks = [...prevState.tasks];
        const idx = tasks.findIndex((t) => t.id === json.taskId);
        if (idx >= 0) {
          tasks[idx] = { ...tasks[idx], status: json.status, detail: json.detail };
        } else {
          tasks.push({
            id: json.taskId,
            label: json.label,
            status: json.status,
            detail: json.detail
          });
        }
        return { tasks };
      });
    } else if (type === "tool_result") {
      // Update tool call with result
      this.setState((prevState) => {
        const msgs = [...prevState.messages];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === "assistant" && msgs[lastIdx].toolCalls) {
          const toolCalls = msgs[lastIdx].toolCalls.map((tc) => {
            if (tc.id === json.toolCallId) {
              return { ...tc, result: json.result, status: "complete" };
            }
            return tc;
          });
          msgs[lastIdx] = { ...msgs[lastIdx], toolCalls };
        }
        return { messages: msgs };
      });
    }
  }

  // Upserts a per-tool activity row (keyed by id) and keeps only the most recent
  // few — the live feed is about "what's happening now", not a full audit log.
  handleActivityEvent(json) {
    if (!json.label) return;
    this.setState((prevState) => {
      const feed = [...prevState.deployActivity];
      const idx = json.id != null ? feed.findIndex((a) => a.id === json.id) : -1;
      if (idx >= 0) {
        feed[idx] = { ...feed[idx], status: json.status };
      } else {
        feed.push({ id: json.id, label: json.label, status: json.status });
      }
      return { deployActivity: feed.slice(-5) };
    });
  }

  // Flips the failing step to a NON-terminal "retrying" state with a sub-line
  // that includes the actual error, so a transient compile failure reads as
  // "being fixed" rather than a dead end the learner should abandon.
  handleStepRetry(json) {
    const summary = retryDetail(json);
    this.setState((prevState) => {
      if (prevState.tasks.length === 0) return null;
      let idx = prevState.tasks.findIndex((t) => t.id === json.step);
      if (idx < 0) {
        idx = prevState.tasks.findIndex((t) => t.status === "in_progress" || t.status === "error");
      }
      if (idx < 0) return null;
      const tasks = prevState.tasks.map((t, i) =>
        i === idx ? { ...t, status: "retrying", detail: summary } : t
      );
      return { tasks };
    });
  }

  handleDeployStep(statusData) {
    const step = statusData.step;
    const isError = ERROR_STEPS.includes(step);
    const isDone = step === "done";

    // Map deploy steps to task progression format
    this.setState((prevState) => {
      // Reset tasks if starting a new attempt (extracting after error/retry/done).
      // `retrying` counts as a prior failure so the next `extracting` rebuilds
      // the list for the fresh attempt.
      const hadError = prevState.tasks.some((t) => t.status === "error" || t.status === "retrying");
      const shouldReset = prevState.tasks.length === 0 || (step === "extracting" && hadError);
      let tasks = shouldReset
        ? DEPLOY_STEPS.map((s) => ({ id: s.id, label: s.label, status: "pending" }))
        : [...prevState.tasks];

      // Show skill banner during deploy
      let activeSkill = prevState.activeSkill;

      if (!isDone && !isError && !activeSkill) {
        activeSkill = { id: "compile_deploy", name: "Publishing" };
      }

      if (isDone) {
        // Mark all as complete
        tasks = tasks.map((t) => ({ ...t, status: "complete" }));
        activeSkill = null;
      } else if (isError) {
        // Find the current in-progress step and mark as error
        const currentIdx = tasks.findIndex((t) => t.status === "in_progress");

        if (currentIdx >= 0) {
          tasks[currentIdx] = {
            ...tasks[currentIdx],
            status: "error",
            detail: statusData.message
          };
        }
        activeSkill = null;
      } else {
        // Find the step index and mark previous as complete, current as in_progress
        const stepIdx = tasks.findIndex((t) => t.id === step);

        if (stepIdx >= 0) {
          tasks = tasks.map((t, i) => {
            if (i < stepIdx) return { ...t, status: "complete" };
            if (i === stepIdx) return { ...t, status: "in_progress", detail: statusData.message };
            return t;
          });
        }
      }

      return { tasks, activeSkill };
    });
  }

  handleDeployResult(data) {
    if (data.agent_session_token) {
      store.dispatch(setAgentSessionToken(data.agent_session_token));
    }
  }

  mergeToolCallDeltas(existing, deltas) {
    const toolCalls = [...existing];
    for (const delta of deltas) {
      const idx = delta.index;

      if (!toolCalls[idx]) {
        toolCalls[idx] = {
          id: delta.id || "tc-" + idx,
          name: (delta.function && delta.function.name) || "",
          arguments: "",
          status: "running"
        };
      }
      if (delta.id) toolCalls[idx].id = delta.id;
      if (delta.function) {
        if (delta.function.name) toolCalls[idx].name = delta.function.name;
        if (delta.function.arguments) {
          toolCalls[idx].arguments += delta.function.arguments;
        }
      }
    }
    return toolCalls;
  }

  updateAssistantMessage(content, toolCalls) {
    this.setState((prevState) => {
      const msgs = [...prevState.messages];
      const lastIdx = msgs.length - 1;

      if (lastIdx >= 0 && msgs[lastIdx].role === "assistant") {
        const prev = msgs[lastIdx];
        const artifactsOrder = prev.artifactsOrder || [];
        const nextOrder =
          content && !artifactsOrder.includes("content") ? [...artifactsOrder, "content"] : artifactsOrder;
        msgs[lastIdx] = {
          ...prev,
          content,
          toolCalls: toolCalls.length > 0 ? toolCalls : prev.toolCalls,
          artifactsOrder: nextOrder
        };
      } else {
        msgs.push({
          id: this.generateId(),
          role: "assistant",
          content,
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          createdAt: new Date(),
          artifactsOrder: content ? ["content"] : []
        });
      }
      return { messages: msgs };
    });
  }

  // Merges structured fields onto the trailing assistant message, creating an
  // empty assistant message if the latest message isn't already from the
  // assistant. Stage events (script_ready, compile_error, deploy_error) can
  // arrive BEFORE any assistant text has streamed — e.g. when the LLM calls a
  // tool as its first action — so we can't assume a bubble already exists.
  // A later content delta lands on the same bubble via updateAssistantMessage,
  // preserving these fields.
  mergeIntoAssistantMessage(fields, artifactKey) {
    this.setState((prevState) => {
      const msgs = [...prevState.messages];
      const lastIdx = msgs.length - 1;
      const appendOrder = (order) => {
        if (!artifactKey || order.includes(artifactKey)) return order;
        return [...order, artifactKey];
      };

      if (lastIdx >= 0 && msgs[lastIdx].role === "assistant") {
        const prev = msgs[lastIdx];
        msgs[lastIdx] = {
          ...prev,
          ...fields,
          artifactsOrder: appendOrder(prev.artifactsOrder || [])
        };
      } else {
        msgs.push({
          id: this.generateId(),
          role: "assistant",
          content: "",
          createdAt: new Date(),
          artifactsOrder: appendOrder([]),
          ...fields
        });
      }
      return { messages: msgs };
    });
  }

  attachScriptArtifact(script, developerName) {
    this.mergeIntoAssistantMessage(
      { scriptArtifact: { script: script, developerName: developerName || null } },
      "scriptArtifact"
    );
  }

  attachCompilationError(errors) {
    this.mergeIntoAssistantMessage({ compilationError: { errors: errors } }, "compilationError");
  }

  attachDeployError(rawMessage, code) {
    this.mergeIntoAssistantMessage({ deployError: { rawMessage: rawMessage, code: code } }, "deployError");
  }

  // ─── Sub-component Renderers ───────────────────────────────────────────

  renderToolCallCard(toolCall, idx) {
    const statusLabel = toolCall.status === "complete" ? "Done" : toolCall.status === "error" ? "Error" : "Running";
    const statusClass = "agent-chat__tool-call-status--" + toolCall.status;

    let formattedArgs = toolCall.arguments || "";
    try {
      if (formattedArgs) {
        formattedArgs = JSON.stringify(JSON.parse(formattedArgs), null, 2);
      }
    } catch (e) {
      // Use raw string
    }

    // Tool call cards are not expandable by default in this simplified version
    // to avoid needing internal state for each card. We show a summary.
    return (
      <div key={toolCall.id || idx} className={"agent-chat__tool-call agent-chat__tool-call--" + toolCall.status}>
        <div className="agent-chat__tool-call-header">
          <span className="agent-chat__tool-call-header-left">
            {toolCall.status === "running" ? (
              <span className="agent-chat__tool-call-spinner" aria-hidden="true" />
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            )}
            <span className="agent-chat__tool-call-name">{toolCall.name}</span>
          </span>
          <span className="agent-chat__tool-call-header-right">
            <span className={"agent-chat__tool-call-status " + statusClass}>{statusLabel}</span>
          </span>
        </div>
        {toolCall.result && (
          <div className="agent-chat__tool-call-body">
            <pre className="agent-chat__tool-call-code">{toolCall.result}</pre>
          </div>
        )}
        {toolCall.error && (
          <div className="agent-chat__tool-call-body">
            <p className="agent-chat__tool-call-error">{toolCall.error}</p>
          </div>
        )}
      </div>
    );
  }

  // T1-9 (Design QA 2026-06-15): the light-blue "Compile & Deploy" skill banner
  // that flashed at the top of the panel read as a stray header ("weird header
  // with a light blue background that then disappeared"). The inline
  // PlanningWorklist already shows the "Compile & Deploy" title with live step
  // progress, so the banner was redundant. `activeSkill` is still tracked in
  // state and drives the worklist title; we just no longer render this banner.
  renderSkillBanner() {
    return null;
  }

  renderUserBubble(msg) {
    return (
      <UserTurn key={msg.id} meta={msg.createdAt ? "Sent: " + formatAgentforceChatTime(msg.createdAt) : null}>
        {msg.content}
      </UserTurn>
    );
  }

  // Show the thinking indicator only while the agent is still working — once
  // streaming finishes, the bubble's text is the answer and the indicator
  // would otherwise sit stale. The planning worklist supersedes it once
  // backend-reported deploy tasks arrive.
  renderAssistantWorklist(msg, isLast) {
    if (!isLast) return null;
    const { agentStages, tasks, activeSkill, status, deployActivity } = this.state;
    const isStreaming = status === STATUS_STREAMING || status === STATUS_CONNECTING;
    if (tasks.length > 0) {
      return (
        <div key={msg.id + "-planning-worklist"} className="tds2-agentforce-modal__draft-worklist-row">
          <PlanningWorklist
            title={(activeSkill && activeSkill.name) || "Publishing"}
            steps={tasksToWorklistSteps(tasks)}
          />
          {isStreaming && deployActivity.length > 0 ? (
            <ThinkingIndicator labels={deployActivity.map((a) => a.label)} />
          ) : null}
        </div>
      );
    }
    if (isStreaming && (agentStages.length > 0 || deployActivity.length > 0)) {
      // No six-step worklist this turn (the prompt didn't trigger a
      // compile/deploy), but the agent can still be doing real work for minutes.
      // Surface the thinking stages AND the live tool activity so the learner
      // sees the current action instead of a frozen "Understanding your request".
      const labels = [...stagesToTraceSteps(agentStages), ...deployActivity.map((a) => a.label)];
      return (
        <div key={msg.id + "-thinking"} className="tds2-agentforce-modal__draft-worklist-row">
          <ThinkingIndicator labels={labels} />
        </div>
      );
    }
    return null;
  }

  renderAssistantArtifacts(msg) {
    const artifacts = [];
    // A retry streams into this same bubble, so the old error card stays on
    // screen. Drop its button while a turn is in flight — the missing button is
    // the cue that the retry is running, and it can't be tapped again.
    const { status } = this.state;
    const onRetry = status === STATUS_STREAMING || status === STATUS_CONNECTING ? undefined : this.handleRetry;
    // Only surface the collapsible thinking trace when there's more than one
    // step to hide behind it. A single step (e.g. just "Understanding your
    // request") is noise — there is nothing to expand (T1-7).
    if (msg.thinkingTrace && msg.thinkingTrace.steps.length > 1) {
      artifacts.push(
        <ThinkingSummaryDisclosure key="thinking" summary={msg.thinkingTrace.summary} steps={msg.thinkingTrace.steps} />
      );
    }

    const renderers = {
      content: () => (msg.content ? <ChatMarkdown key="content" content={msg.content} /> : null),
      scriptArtifact: () =>
        msg.scriptArtifact ? (
          <CodeArtifactCard
            key="script"
            title={msg.scriptArtifact.developerName ? msg.scriptArtifact.developerName + ".agent" : "AgentScript"}
            language="agentscript"
            body={msg.scriptArtifact.script}
          />
        ) : null,
      compilationError: () =>
        msg.compilationError ? (
          <CompilationErrorState
            key="compilation-error"
            errors={msg.compilationError.errors}
            onRetry={onRetry}
          />
        ) : null,
      deployError: () =>
        msg.deployError ? (
          <DeployErrorState
            key="deploy-error"
            rawMessage={msg.deployError.rawMessage}
            code={msg.deployError.code}
            onRetry={onRetry}
          />
        ) : null
    };

    // Render artifacts in the order they arrived during streaming so the
    // AGENTSCRIPT card sits where the LLM emitted it (often before the
    // narrative text) instead of being pinned beneath the markdown.
    // Fall back to the legacy fixed order for messages without an order
    // (e.g. restored sessions, which only carry role + content).
    const order =
      msg.artifactsOrder && msg.artifactsOrder.length > 0
        ? msg.artifactsOrder
        : ["content", "scriptArtifact", "compilationError", "deployError"];
    for (const key of order) {
      const node = renderers[key] && renderers[key]();
      if (node) artifacts.push(node);
    }
    return artifacts;
  }

  renderAssistantBubble(msg, isLast) {
    // MVP decision (launch copy doc): the assistant turn carries no agent name —
    // just the timestamp. The `agentName` prop is retained for API stability and
    // a possible future named persona, but is intentionally not rendered here.
    const meta = msg.createdAt ? formatAgentforceChatTime(msg.createdAt) : null;
    const hasToolCalls = msg.toolCalls && msg.toolCalls.length > 0;
    // The DS AgentforceMFEModalAgentTurn has no dedicated `extras` slot (unlike the
    // retired shim). Tool-call cards are self-contained block-level elements, so we
    // append them to the turn's children — they render in the copy area's normal
    // block flow after the artifacts, not in the inline-flex `actions` row.
    const toolCallCards = hasToolCalls ? msg.toolCalls.map((tc, idx) => this.renderToolCallCard(tc, idx)) : null;
    return (
      <React.Fragment key={msg.id}>
        {this.renderAssistantWorklist(msg, isLast)}
        <AgentTurn meta={meta}>
          {this.renderAssistantArtifacts(msg)}
          {toolCallCards}
        </AgentTurn>
      </React.Fragment>
    );
  }

  renderMessageBubble(msg, isLast) {
    if (msg.role === "user") return this.renderUserBubble(msg);
    return this.renderAssistantBubble(msg, isLast);
  }

  renderErrorRow() {
    const { status, error } = this.state;
    if (status !== STATUS_ERROR) return null;

    if (error && LLM_GATEWAY_ERROR_REGEX.test(error)) {
      return (
        <LlmGatewayErrorState rawMessage={error} mascotUrl={this.props.errorMascotUrl} onRetry={this.handleRetry} />
      );
    }

    return (
      <ConnectionErrorState
        message={error || undefined}
        mascotUrl={this.props.errorMascotUrl}
        onReconnect={this.handleRetry}
        logsHref={this.props.runtimeLogsUrl || undefined}
      />
    );
  }

  // Test-mode "Reset session" affordance, gated by the allowSessionReset prop.
  // Shown both in the active chat and on the locked success state so testers can
  // unstick a completed session. Returns null when reset isn't allowed or a turn
  // is in flight.
  renderResetButton(isStreaming) {
    if (!this.props.allowSessionReset || isStreaming) return null;
    return (
      <div className="agent-chat__reset-row">
        <button type="button" className="agent-chat__reset-button" onClick={this.handleResetSession}>
          Reset session (test mode)
        </button>
      </div>
    );
  }

  renderWelcome() {
    const { welcomeConfig } = this.props;

    // A unit has configured its own welcome when it declares a title, a
    // subtitle, or at least one pill — mirroring Agent::UnitCurriculum.welcome's
    // own definition server-side. Gating on pills alone meant a unit with
    // custom copy but ZERO starter pills (an intentional "just describe it"
    // experience) fell all the way through to the generic default below,
    // silently discarding its title/subtitle — the Trick-or-Trailhead
    // regression behind W-24059362.
    const isConfigured =
      welcomeConfig && (welcomeConfig.title || welcomeConfig.subtitle ||
        (welcomeConfig.pills && welcomeConfig.pills.length > 0));

    // When the unit declares a welcome (agent_chat.welcome → welcomeConfig),
    // render its pills, if any. Each pill pre-fills its `prompt` and carries its
    // optional `track` (sent to the controller as agent_track). This is how the
    // persona — one "Help Agent" vs. the legacy Sales/Service tracks — is unit
    // content rather than code.
    if (isConfigured) {
      const pills = (welcomeConfig.pills || []).map((pill, i) => ({
        key: `welcome-pill-${i}`,
        label: pill.label,
        variant: pill.variant || "secondary",
        icon: pill.icon || undefined,
        onClick: () => this.handlePromptPill(pill.prompt, pill.track || null)
      }));

      // Pass title/subtitle as empty strings (not undefined) when the unit omits
      // them, so they render blank rather than falling through to WelcomeState's
      // legacy "Pick a track below…" defaultProps — a partially-configured unit
      // must never resurface the Sales/Service track copy this rename removes.
      return (
        <WelcomeState
          mascotUrl={this.props.welcomeMascotUrl}
          title={welcomeConfig.title || ""}
          subtitle={welcomeConfig.subtitle || ""}
          pills={pills}
        />
      );
    }

    // No unit-configured welcome: render the DEFAULT single "Build a Help Agent"
    // pill, matching the headless-agent-creation prototype's ConnectedReadyState
    // (one pill that pre-fills the comprehensive support-agent starter prompt).
    // The legacy Sales/Service/Help-Me-Choose three-pill model is retired as the
    // default; a unit can still declare its own welcome.pills to override this.
    const pills = [
      {
        key: "help-agent",
        label: "Build a Help Agent",
        variant: "secondary",
        onClick: () => this.handlePromptPill(DEFAULT_HELP_AGENT_PROMPT, "service")
      }
    ];

    return (
      <WelcomeState
        mascotUrl={this.props.welcomeMascotUrl}
        title="Build an agent through conversation."
        subtitle="Ready to build? Describe your Help Agent below to get started using natural language."
        pills={pills}
      />
    );
  }

  // ─── Main Render ───────────────────────────────────────────────────────

  render() {
    const { messages, inputValue, status, locked } = this.state;
    const isStreaming = status === STATUS_STREAMING || status === STATUS_CONNECTING;
    const { isAuthenticated, hasConnectedOrg, poolHasCapacity } = this.props;
    const canChat = isAuthenticated && hasConnectedOrg;
    // A locked session is a completed challenge: never resurface the welcome
    // screen (the learner already built + deployed an agent) and never let the
    // composer accept input.
    const showWelcome = messages.length === 0 && !locked;

    const stopButton = (
      <button
        type="button"
        className="tds2-agentforce__stop-button"
        onClick={this.handleAbort}
        aria-label="Stop generating"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="1" />
        </svg>
      </button>
    );

    // A locked (completed) session returns the success panel below before the
    // composer is ever rendered, so the placeholder/inputBar only cover the
    // active states (chat, no-org, logged-out).
    const placeholder = canChat
      ? this.props.placeholder
      : isAuthenticated
        ? NO_ORG_PLACEHOLDER
        : LOGGED_OUT_PLACEHOLDER;

    const inputBar = (
      <AgentChatInputBar
        inputId="agent-chat-input"
        placeholder={placeholder}
        value={inputValue}
        onValueChange={(next) => this.setState({ inputValue: next })}
        onSend={() => this.handleSend()}
        disabled={isStreaming || !canChat}
        disableSendUnlessNonempty
        hideDisclaimer={this.props.hideDisclaimer}
        sendButton={isStreaming ? stopButton : null}
        multiline
      />
    );

    if (!isAuthenticated) {
      return (
        <div className="agent-chat">
          <UnauthenticatedState mascotUrl={this.props.welcomeMascotUrl} loginUrl={this.props.loginUrl} />
          {inputBar}
        </div>
      );
    }

    if (!hasConnectedOrg) {
      // PRE-admission capacity gate. Provisioning a playground is slow and
      // consumes real org-pool capacity, and the concurrency seat isn't taken
      // until the learner's first agentic turn (chat#create) — AFTER the org
      // exists. So if the sitewide pool is already full, show the calm "at
      // capacity" panel INSTEAD of the create-playground panel: the learner never
      // sees the "Create Playground" button, so no org is minted for someone the
      // first-turn gate would immediately reject. `poolHasCapacity` is a
      // server-computed snapshot (non-consuming, fails open); admit! stays the
      // authoritative gate, and "Try again" reloads to re-take the peek. Only
      // reached in agent-chat units — this whole component is the agent-chat panel.
      if (!poolHasCapacity) {
        return (
          <div className="agent-chat">
            <OverloadedState
              mascotUrl={this.props.welcomeMascotUrl}
              onRetry={this.handleCapacityRecheck}
            />
          </div>
        );
      }

      return (
        <div className="agent-chat">
          <NoOrgState
            mascotUrl={this.props.welcomeMascotUrl}
            helpUrl={this.props.helpUrl}
            unitUid={this.props.unitId}
            orgRequestState={this.props.orgRequestState}
            orgRequestStatusUrl={this.props.orgRequestStatusUrl}
            orgRequestAutoLogin={this.props.orgRequestAutoLogin}
            errorMessage={this.props.errorMessage}
          />
          {inputBar}
        </div>
      );
    }

    // A locked session is a completed challenge. The learner already built and
    // deployed an agent, so we replace the whole panel with the celebratory
    // success state and drop the composer entirely (the server refuses further
    // turns on a locked session anyway). The test-mode reset affordance stays so
    // testers can unstick a locked session.
    if (locked) {
      return (
        <div className="agent-chat">
          {this.renderResetButton(isStreaming)}
          <DeploySuccessState
            mascotUrl={this.props.successMascotUrl}
            showSharing={this.props.showSharing}
            sharing={this.props.sharing}
          />
        </div>
      );
    }

    // Sitewide concurrency pool at capacity: a turn was refused with a 503
    // `pool_full`, so the learner holds no seat. Replace the whole panel with the
    // calm "busy right now" state and drop the composer — there's no live turn to
    // stop and no seat to type into. Placed before the welcome branch so it takes
    // over the surface uniformly. Anything already earned is safe (LO rows +
    // session locks persist server-side); this only gates entry to a NEW turn, and
    // "Try again" re-attempts admission. The reset affordance stays (dev/test only,
    // self-gated) so testers can unstick.
    if (this.state.poolFull) {
      return (
        <div className="agent-chat">
          {this.renderResetButton(isStreaming)}
          <OverloadedState
            mascotUrl={this.props.welcomeMascotUrl}
            onRetry={this.handlePoolRetry}
            retrying={isStreaming}
          />
        </div>
      );
    }

    if (showWelcome) {
      return (
        <div className="agent-chat">
          {this.renderSkillBanner()}
          {this.renderWelcome()}
          {inputBar}
        </div>
      );
    }

    const lastMsg = messages[messages.length - 1];
    const lastMsgIsAssistant = lastMsg && lastMsg.role === "assistant";
    const { tasks, activeSkill, agentStages, deployActivity } = this.state;
    const showStandaloneWorklist = !lastMsgIsAssistant && tasks.length > 0;

    // The stage worklist normally renders inside the assistant bubble (see
    // renderMessageBubble), but between submit and the first streamed token —
    // or during a long leading run of tool calls that streams no text — the
    // last message is still the user's, so no bubble exists yet. Surface the
    // backend stages AND live tool activity as a standalone row so the user sees
    // in-progress feedback (not a frozen "Understanding your request") instead
    // of a silent, disabled input.
    const showStandaloneStages =
      !lastMsgIsAssistant && tasks.length === 0 && (agentStages.length > 0 || deployActivity.length > 0);

    return (
      <div className="agent-chat">
        {this.renderSkillBanner()}

        {this.renderResetButton(isStreaming)}

        <ScrollBody
          ref={(el) => {
            this.scrollRef = el;
          }}
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
          className="tds2-agentforce-modal__canvas--flat"
        >
          <div className="tds2-agentforce-modal__thread tds2-agentforce-modal__thread--flat">
            {messages.map((msg, idx) => this.renderMessageBubble(msg, idx === messages.length - 1))}

            {showStandaloneWorklist && (
              <div className="tds2-agentforce-modal__draft-worklist-row">
                <PlanningWorklist
                  title={(activeSkill && activeSkill.name) || "Publishing"}
                  steps={tasksToWorklistSteps(tasks)}
                />
              </div>
            )}

            {showStandaloneStages && (
              <div className="tds2-agentforce-modal__draft-worklist-row">
                <ThinkingIndicator
                  labels={[...stagesToTraceSteps(agentStages), ...deployActivity.map((a) => a.label)]}
                />
              </div>
            )}

            {this.renderErrorRow()}

            <div
              ref={(el) => {
                this.endRef = el;
              }}
              className="agent-chat__end-sentinel"
              aria-hidden="true"
            />
          </div>
        </ScrollBody>

        {inputBar}
      </div>
    );
  }
}

export default ChatContainer;
